/* eslint-disable react/no-unknown-property */
/**
 * TemplateCut — saddle-cut layout for tubular intersections.
 *
 * Workflow (Feb 2026):
 *   1. Scan Circle A on the parent pipe (≥3 shots).
 *   2. Scan Circle B on the parent pipe on the other side of the hole.
 *   3. App derives host OD + axis + orientation. Show for confirm.
 *   4. Measure intersection centreline point (1 shot).
 *   5. Enter branch OD, angle (default 90° = perp) and pick direction
 *      (Toward A / Toward B) — 3D preview updates live.
 *   6. Laser-trace the cut curve.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, Crosshair, Trash, Aperture, RotateCcw } from "lucide-react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Line } from "@react-three/drei";
import * as THREE from "three";
import { api, station } from "../lib/api";
import { ToolSplitLayout } from "../components/ToolSplitLayout";
import { PrintReportShell, PrintReportButton } from "../components/PrintReportShell";
import { AimPad } from "../components/AimPad";

const tpApi = {
  list: () => api.get("/template/sessions").then((r) => r.data),
  create: (b) => api.post("/template/sessions", b).then((r) => r.data),
  get: (id) => api.get(`/template/sessions/${id}`).then((r) => r.data),
  del: (id) => api.delete(`/template/sessions/${id}`).then((r) => r.data),
  geom: (id, b) => api.patch(`/template/sessions/${id}/geometry`, b).then((r) => r.data),
  addA: (id, b) => api.post(`/template/sessions/${id}/circle-a/shots`, b).then((r) => r.data),
  addB: (id, b) => api.post(`/template/sessions/${id}/circle-b/shots`, b).then((r) => r.data),
  delA: (id, sid) => api.delete(`/template/sessions/${id}/circle-a/shots/${sid}`).then((r) => r.data),
  delB: (id, sid) => api.delete(`/template/sessions/${id}/circle-b/shots/${sid}`).then((r) => r.data),
  setIntersect: (id, b) => api.post(`/template/sessions/${id}/intersection`, b).then((r) => r.data),
  clearIntersect: (id) => api.delete(`/template/sessions/${id}/intersection`).then((r) => r.data),
};

/* ------------------------------------------------------------------ */
/*  2-D unfolded template preview (unchanged from prior version)      */
/* ------------------------------------------------------------------ */
const TemplatePreview = ({ points, hostOD, cutOD }) => {
  if (!points.length) return null;
  const us = points.map((p) => p[0]);
  const vs = points.map((p) => p[1]);
  const minU = Math.min(...us), maxU = Math.max(...us);
  const minV = Math.min(...vs), maxV = Math.max(...vs);
  const w = maxU - minU || 1;
  const h = maxV - minV || 1;
  const pad = 26, svgW = 480, svgH = 240;
  const scale = Math.min((svgW - 2 * pad) / w, (svgH - 2 * pad) / h);
  const tx = (u) => pad + (u - minU) * scale;
  const ty = (v) => svgH - (pad + (v - minV) * scale);
  return (
    <div>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full rounded-md border border-[#2A2E39] bg-[#0A0B0E]">
        <line x1={pad} y1={svgH - pad} x2={svgW - pad} y2={svgH - pad}
              stroke="#2A2E39" strokeDasharray="4 4" />
        <polyline
          points={points.map((p) => `${tx(p[0])},${ty(p[1])}`).join(" ")}
          fill="none" stroke="#00E1C7" strokeWidth={2}
        />
        <text x={svgW - pad} y={16} fontSize={11} fill="#8B939A"
              textAnchor="end" fontFamily="JetBrains Mono, monospace">
          Ø host {hostOD.toFixed(1)} · Ø cut {cutOD} mm
        </text>
      </svg>
      <div className="mt-1 text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
        Unfolded template — U (arc around cut tube) × V (length along cut tube axis)
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  3-D scene — host cylinder + branch cylinder + cut curve           */
/* ------------------------------------------------------------------ */

/**
 * Build a THREE Object3D-friendly cylinder axis using two world points
 * (start, end) and a diameter (m). Returns { position, quaternion,
 * length } — apply to a <cylinderGeometry /> which lies along +Y by
 * default.
 */
function cylinderTransform(a, b) {
  const start = new THREE.Vector3(a.x, a.y, a.z);
  const end = new THREE.Vector3(b.x, b.y, b.z);
  const dir = end.clone().sub(start);
  const length = dir.length();
  if (length < 1e-9) return null;
  const mid = start.clone().add(end).multiplyScalar(0.5);
  const up = new THREE.Vector3(0, 1, 0);
  const quat = new THREE.Quaternion().setFromUnitVectors(up, dir.clone().normalize());
  return { position: mid, quaternion: quat, length };
}

const HostCylinder = ({ centerA, centerB, radius }) => {
  const t = useMemo(() => cylinderTransform(centerA, centerB), [centerA, centerB]);
  if (!t) return null;
  // Extend both ends a bit so the pipe doesn't stop right at scan planes.
  const extended = t.length * 1.4;
  return (
    <mesh position={t.position.toArray()} quaternion={t.quaternion.toArray()}>
      <cylinderGeometry args={[radius, radius, extended, 64, 1, true]} />
      <meshStandardMaterial color="#3a4249" metalness={0.6} roughness={0.5} side={THREE.DoubleSide} transparent opacity={0.85} />
    </mesh>
  );
};

const BranchCylinder = ({ origin, axisDir, radius, length }) => {
  const start = useMemo(() => {
    // Start just inside the host (radius of host) so branch appears to stub in.
    const s = new THREE.Vector3(origin.x, origin.y, origin.z);
    const dir = new THREE.Vector3(axisDir.x, axisDir.y, axisDir.z).normalize();
    return s.clone().add(dir.multiplyScalar(-radius * 0.5)).toArray();
  }, [origin, axisDir, radius]);
  const end = useMemo(() => {
    const s = new THREE.Vector3(origin.x, origin.y, origin.z);
    const dir = new THREE.Vector3(axisDir.x, axisDir.y, axisDir.z).normalize();
    return s.clone().add(dir.multiplyScalar(length)).toArray();
  }, [origin, axisDir, length]);
  const t = useMemo(
    () => cylinderTransform({ x: start[0], y: start[1], z: start[2] }, { x: end[0], y: end[1], z: end[2] }),
    [start, end]
  );
  if (!t) return null;
  return (
    <mesh position={t.position.toArray()} quaternion={t.quaternion.toArray()}>
      <cylinderGeometry args={[radius, radius, t.length, 48, 1, true]} />
      <meshStandardMaterial color="#F5A623" metalness={0.5} roughness={0.4} side={THREE.DoubleSide} transparent opacity={0.85} />
    </mesh>
  );
};

const CircleMarker = ({ center, normal, radius, color }) => {
  // Build a 3D ring by sampling around a basis on the plane whose normal is `normal`.
  const pts = useMemo(() => {
    if (!center || !normal || !radius) return [];
    const n = new THREE.Vector3(normal.x, normal.y, normal.z).normalize();
    let u = new THREE.Vector3(1, 0, 0);
    if (Math.abs(n.dot(u)) > 0.9) u = new THREE.Vector3(0, 1, 0);
    const uu = new THREE.Vector3().crossVectors(n, u).normalize();
    const vv = new THREE.Vector3().crossVectors(n, uu).normalize();
    const out = [];
    for (let i = 0; i <= 64; i++) {
      const a = (i / 64) * Math.PI * 2;
      const p = new THREE.Vector3(center.x, center.y, center.z)
        .add(uu.clone().multiplyScalar(Math.cos(a) * radius))
        .add(vv.clone().multiplyScalar(Math.sin(a) * radius));
      out.push(p.toArray());
    }
    return out;
  }, [center, normal, radius]);
  if (!pts.length) return null;
  return <Line points={pts} color={color} lineWidth={2} />;
};

const CutCurve = ({ pointsWorld }) => {
  const pts = useMemo(
    () => pointsWorld.map((p) => [p.x, p.y, p.z]),
    [pointsWorld]
  );
  if (!pts.length) return null;
  return <Line points={pts} color="#00E1C7" lineWidth={3} />;
};

const AxisGizmo = () => (
  <>
    <axesHelper args={[0.3]} />
    <gridHelper args={[2, 20, 0x2a2e39, 0x2a2e39]} />
  </>
);

const Scene3D = ({ session }) => {
  const {
    circle_a_fit: aFit, circle_b_fit: bFit,
    host_center_a: hA, host_center_b: hB,
    resolved_origin: origin, resolved_axis: axis,
    host_tube_od_mm, cut_tube_od_mm,
    points_world,
  } = session;
  const hostR = host_tube_od_mm / 2000.0;
  const cutR = cut_tube_od_mm / 2000.0;
  // Choose a camera target based on scan geometry, fallback to origin.
  const target = useMemo(() => {
    if (origin) return [origin.x, origin.y, origin.z];
    if (hA) return [hA.x, hA.y, hA.z];
    return [0, 0, 0];
  }, [origin, hA]);
  // Branch length for preview: 2× host OD is a nice default.
  const branchLen = Math.max(hostR * 4, 0.5);

  return (
    <Canvas
      camera={{ position: [target[0] + 1.5, target[1] + 1.5, target[2] + 1.0], fov: 45 }}
      style={{ background: "#0A0B0E" }}
    >
      <ambientLight intensity={0.5} />
      <directionalLight position={[3, 5, 4]} intensity={0.8} />
      <directionalLight position={[-3, -1, -4]} intensity={0.3} color="#00E1C7" />
      <OrbitControls target={target} makeDefault />
      <AxisGizmo />
      {hA && hB && hostR > 0 && (
        <HostCylinder centerA={hA} centerB={hB} radius={hostR} />
      )}
      {aFit && (
        <CircleMarker center={aFit.center} normal={aFit.normal} radius={aFit.radius_m} color="#009E8E" />
      )}
      {bFit && (
        <CircleMarker center={bFit.center} normal={bFit.normal} radius={bFit.radius_m} color="#009E8E" />
      )}
      {origin && axis && cutR > 0 && (
        <BranchCylinder origin={origin} axisDir={axis} radius={cutR} length={branchLen} />
      )}
      {points_world && points_world.length > 0 && (
        <CutCurve pointsWorld={points_world} />
      )}
      {origin && (
        <mesh position={[origin.x, origin.y, origin.z]}>
          <sphereGeometry args={[Math.max(hostR * 0.08, 0.01), 12, 12]} />
          <meshStandardMaterial color="#FF3B30" emissive="#FF3B30" emissiveIntensity={0.6} />
        </mesh>
      )}
    </Canvas>
  );
};

/* ------------------------------------------------------------------ */
/*  Small UI helpers                                                  */
/* ------------------------------------------------------------------ */

const StepBadge = ({ label, state }) => {
  const cls = state === "done"
    ? "border-[#8DC63F] bg-[#8DC63F]/10 text-[#8DC63F]"
    : state === "active"
      ? "border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]"
      : "border-[#2A2E39] bg-[#14161A] text-[#5A6065]";
  return (
    <span
      className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${cls}`}
      style={{ fontFamily: "Chakra Petch, sans-serif" }}
    >
      {state === "done" ? "✓ " : ""}{label}
    </span>
  );
};

const RMSE_WARN_MM = 3.0;   // "the fit is off — bad shot in the ring" threshold
const RMSE_CAUTION_MM = 1.5; // amber

/**
 * Distance (mm) from a measured shot to the nearest point on the fitted
 * 3-D circle. This is the same quantity that the RMSE is aggregated
 * from — a large per-shot residual pinpoints exactly which shot is
 * dragging the fit around.
 *
 * Nearest point on the circle:
 *   1. Project shot into the plane of the circle (drop the component
 *      along the plane normal).
 *   2. In that plane the nearest circle point is
 *      centre + radius · normalise(projected − centre).
 *   3. Residual = |shot − nearest|.
 */
function shotResidualMm(shot, fit) {
  if (!fit) return null;
  const cx = fit.center.x, cy = fit.center.y, cz = fit.center.z;
  const nx = fit.normal.x, ny = fit.normal.y, nz = fit.normal.z;
  const R = fit.radius_m;
  // Project shot into the plane
  const dx = shot.x - cx, dy = shot.y - cy, dz = shot.z - cz;
  const dn = dx * nx + dy * ny + dz * nz;
  const px = dx - dn * nx, py = dy - dn * ny, pz = dz - dn * nz;
  const pMag = Math.sqrt(px * px + py * py + pz * pz);
  if (pMag < 1e-9) {
    // Shot sits on the axis of the circle — worst case, residual == R
    return R * 1000.0;
  }
  const ex = (px / pMag) * R, ey = (py / pMag) * R, ez = (pz / pMag) * R;
  // Vector shot → nearest circle point
  const rx = ex - dx, ry = ey - dy, rz = ez - dz;
  return Math.sqrt(rx * rx + ry * ry + rz * rz) * 1000.0;
}

const ShotRow = ({ shot, onDelete, testid, residualMm }) => {
  const tone =
    residualMm == null
      ? ""
      : residualMm > RMSE_WARN_MM
        ? "border-[#FF3B30]/60 bg-[#FF3B30]/10"
        : residualMm > RMSE_CAUTION_MM
          ? "border-[#F5A623]/50 bg-[#F5A623]/5"
          : "border-[#2A2E39] bg-[#0A0B0E]";
  const resColor =
    residualMm == null
      ? "text-[#5A6065]"
      : residualMm > RMSE_WARN_MM
        ? "text-[#FF3B30]"
        : residualMm > RMSE_CAUTION_MM
          ? "text-[#F5A623]"
          : "text-[#8DC63F]";
  return (
    <div className={`flex items-center justify-between rounded-sm border px-2 py-1 text-[10px] ${tone}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>
      <span className="text-[#B5BEC5]">
        x {(shot.x).toFixed(3)} · y {(shot.y).toFixed(3)} · z {(shot.z).toFixed(3)}
      </span>
      <div className="flex items-center gap-2">
        {residualMm != null && (
          <span
            data-testid={`${testid}-residual`}
            className={`font-mono ${resColor}`}
            title={`Distance from this shot to the fitted circle`}
          >
            Δ {residualMm.toFixed(2)} mm
          </span>
        )}
        <button
          data-testid={testid}
          onClick={onDelete}
          className="text-[#5A6065] hover:text-[#FF3B30]"
        >
          <Trash size={11} />
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

export default function TemplateCut({ onBack }) {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [newName, setNewName] = useState("");
  const [newHost, setNewHost] = useState("200");
  const [newCut, setNewCut] = useState("100");
  const [newAngle, setNewAngle] = useState("90");
  const [busy, setBusy] = useState(false);
  const [tracing, setTracing] = useState(false);
  const [tracingIdx, setTracingIdx] = useState(0);
  // Manual scan-stage — "a" (shooting Circle A), "b" (Circle B),
  // "intersection" (waiting on CL shot), "geometry" (branch inputs).
  // The surveyor advances explicitly with "Done · Move on" buttons.
  // Persisted per-session in localStorage so re-opening a session in
  // the middle of a scan keeps the same stage.
  const [stage, setStage] = useState("a");
  const stageKey = (sid) => `fabaxis:template:${sid}:stage`;

  const refreshList = useCallback(async () => {
    try { setSessions(await tpApi.list()); } catch { /* silent */ }
  }, []);
  useEffect(() => { refreshList(); }, [refreshList]);

  // When the current session changes, seed the stage from localStorage
  // or infer sensibly from the session content.
  useEffect(() => {
    if (!current) return;
    let s = null;
    try { s = localStorage.getItem(stageKey(current.id)); } catch { /* ok */ }
    if (s === "a" || s === "b" || s === "intersection" || s === "geometry") {
      setStage(s);
    } else {
      // Infer: if intersection exists, we're on geometry;
      // if B has shots, we're on intersection (or later);
      // if A has shots but no B, we're on A still;
      // otherwise A.
      if (current.intersection_point) setStage("geometry");
      else if (current.circle_b_shots.length > 0) setStage("intersection");
      else setStage("a");
    }
  }, [current?.id]);

  const advanceStage = (next) => {
    setStage(next);
    if (current) {
      try { localStorage.setItem(stageKey(current.id), next); } catch { /* ok */ }
    }
  };

  /* ------------ session create/open/delete ---------------------- */

  const create = async () => {
    if (!newName.trim()) return toast.error("Name required");
    const s = await tpApi.create({
      name: newName.trim(),
      host_tube_od_mm: parseFloat(newHost) || 200,
      cut_tube_od_mm: parseFloat(newCut) || 100,
      intersect_angle_deg: parseFloat(newAngle) || 90,
    });
    setNewName("");
    await refreshList();
    setCurrent(s);
  };

  /* ------------ measurement helpers ----------------------------- */

  const captureShot = async () => {
    // Use station-local coordinates (derived from hz/v/sd) so the
    // whole template pipeline is independent of the world frame the
    // instrument might or might not have configured on its keypad.
    // See station.measureLocal for the formulas.
    return await station.measureLocal();
  };

  const takeCircleShot = async (which) => {
    if (!current) return;
    setBusy(true);
    try {
      const m = await captureShot();
      const s = which === "a"
        ? await tpApi.addA(current.id, { x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd })
        : await tpApi.addB(current.id, { x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd });
      setCurrent(s);
      toast.success(`Circle ${which.toUpperCase()} shot #${(which === "a" ? s.circle_a_shots : s.circle_b_shots).length}`);
    } catch { toast.error("Measurement failed"); }
    finally { setBusy(false); }
  };

  const deleteCircleShot = async (which, shotId) => {
    if (!current) return;
    try {
      const s = which === "a"
        ? await tpApi.delA(current.id, shotId)
        : await tpApi.delB(current.id, shotId);
      setCurrent(s);
    } catch { /* silent */ }
  };

  const takeIntersection = async () => {
    if (!current) return;
    setBusy(true);
    try {
      const m = await captureShot();
      const s = await tpApi.setIntersect(current.id, { x: m.x, y: m.y, z: m.z });
      setCurrent(s);
      toast.success("Intersection captured");
    } catch { toast.error("Measurement failed"); }
    finally { setBusy(false); }
  };

  const clearIntersection = async () => {
    if (!current) return;
    try {
      const s = await tpApi.clearIntersect(current.id);
      setCurrent(s);
    } catch { /* silent */ }
  };

  /* ------------ geometry (cut od, angle, direction) ------------ */

  const updateGeom = async (patch) => {
    if (!current) return;
    try {
      const s = await tpApi.geom(current.id, patch);
      setCurrent(s);
    } catch { /* silent */ }
  };

  /* ------------ trace ------------------------------------------ */

  /**
   * Convert a station-local XYZ (the frame every shot in this session
   * was captured in via station.measureLocal) into (hz, v) for
   * station.turn(). No station offset needed — everything is relative
   * to the scope pivot.
   *
   * Convention (Leica GeoCOM forward math, inverted):
   *   x =  sd · sin(v) · sin(hz)
   *   y =  sd · sin(v) · cos(hz)
   *   z =  sd · cos(v)
   *   → hz = atan2(x, y)   (clockwise from Y+ / instrument-north)
   *   → v  = atan2(√(x²+y²), z)   (zenith angle)
   */
  const localToAim = (p) => {
    const horiz = Math.hypot(p.x, p.y);
    let hz = Math.atan2(p.x, p.y);
    if (hz < 0) hz += 2 * Math.PI;
    const v = horiz < 1e-9 && Math.abs(p.z) < 1e-9
      ? Math.PI / 2
      : Math.atan2(horiz, p.z);
    return { hz, v };
  };

  const runTrace = async () => {
    if (!current || !current.points_world.length) return;
    setTracing(true); setTracingIdx(0);
    try {
      await station.laser(true);
      for (let i = 0; i < current.points_world.length; i++) {
        setTracingIdx(i);
        const { hz, v } = localToAim(current.points_world[i]);
        try { await station.turn({ hz, v }); } catch { /* keep going */ }
        await new Promise((r) => setTimeout(r, 120));
      }
      toast.success("Trace complete");
    } catch { toast.error("Trace failed"); }
    finally { setTracing(false); setTracingIdx(0); }
  };

  /* ------------ derived flags ---------------------------------- */

  const nA = current?.circle_a_shots?.length ?? 0;
  const nB = current?.circle_b_shots?.length ?? 0;
  const aFitOk = !!current?.circle_a_fit;
  const bFitOk = !!current?.circle_b_fit;
  const hostOk = aFitOk && bFitOk && !!current?.host_axis_dir;
  const intersectOk = !!current?.intersection_point;
  const readyForTrace = hostOk && intersectOk && (current?.points_world?.length ?? 0) > 0;

  /* ------------ session list view ------------------------------ */
  if (!current) {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="template-page">
        <div className="mx-auto max-w-4xl px-4 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={onBack} data-testid="template-back"
              className="flex items-center gap-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E]">
              <ArrowLeft size={12} /> Toolbox
            </button>
            <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">Saddle-cut layout</span>
          </div>
          <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-4">
            <div className="grid gap-3 sm:grid-cols-[1fr,90px,90px,90px,120px]">
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                data-testid="template-new-name"
                placeholder="Saddle ID (e.g. 6&quot; branch on header A)"
                className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none" />
              <input type="number" value={newHost} onChange={(e) => setNewHost(e.target.value)}
                placeholder="Host mm" data-testid="template-new-host"
                className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white text-center" />
              <input type="number" value={newCut} onChange={(e) => setNewCut(e.target.value)}
                placeholder="Cut mm" data-testid="template-new-cut"
                className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white text-center" />
              <input type="number" value={newAngle} onChange={(e) => setNewAngle(e.target.value)}
                placeholder="Angle °" data-testid="template-new-angle"
                className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white text-center" />
              <button onClick={create} data-testid="template-create"
                className="flex items-center justify-center gap-2 rounded-sm border border-[#009E8E] bg-[#009E8E]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20">
                <Plus size={12} strokeWidth={2.5} /> Create
              </button>
            </div>
            <div className="mt-2 text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              Host OD/cut OD/angle are seed defaults; the app will scan the host to derive the real OD and axis.
            </div>
          </div>
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-md border border-[#2A2E39] bg-[#14161A] p-3 hover:border-[#009E8E]">
                <button className="flex-1 text-left" onClick={async () => setCurrent(await tpApi.get(s.id))} data-testid={`template-open-${s.id}`}>
                  <div className="font-bold text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>{s.name}</div>
                  <div className="mt-1 text-[10px] text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    Ø{s.host_od_mm} host · Ø{s.cut_od_mm} cut @ {s.angle_deg}° · A {s.n_a} · B {s.n_b} · {s.has_intersection ? "CL✓" : "CL—"} · {s.orientation || "—"}
                  </div>
                </button>
                <button onClick={async () => { await tpApi.del(s.id); refreshList(); }}
                  className="rounded-sm border border-transparent p-1.5 text-[#5A6065] hover:border-[#FF3B30] hover:text-[#FF3B30]">
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ------------ session view ----------------------------------- */

  return (
    <ToolSplitLayout
      data-testid="template-session"
      title={current.name}
      subtitle={
        readyForTrace
          ? `Ø${current.host_tube_od_mm.toFixed(1)} host · Ø${current.cut_tube_od_mm} cut · ${current.intersect_angle_deg.toFixed(1)}° · ${current.host_orientation}`
          : `A ${nA} · B ${nB} · ${intersectOk ? "CL✓" : "CL—"}`
      }
      left={
        <div className="space-y-4">
          <PrintReportShell
            toolName="Template Cut"
            sessionName={current.name}
            passFail={readyForTrace ? "PASS" : null}
            headline={readyForTrace
              ? `Ø${current.host_tube_od_mm.toFixed(1)} host · Ø${current.cut_tube_od_mm} cut · ${current.intersect_angle_deg.toFixed(1)}°`
              : "Template not ready"
            }
            surveyor="—"
          >
          {readyForTrace && (
            <div className="flex items-center justify-end gap-2 no-print">
              <PrintReportButton testid="template-print-btn" />
            </div>
          )}
          {/* --------------- Stepper --------------- */}
          <div className="flex flex-wrap items-center gap-2">
            <StepBadge label="1. Circle A" state={stage === "a" ? "active" : (nA >= 3 ? "done" : "pending")} />
            <StepBadge label="2. Circle B" state={stage === "b" ? "active" : (nB >= 3 ? "done" : "pending")} />
            <StepBadge label="3. Host derived" state={hostOk ? "done" : "pending"} />
            <StepBadge label="4. Intersection" state={stage === "intersection" ? "active" : (intersectOk ? "done" : "pending")} />
            <StepBadge label="5. Branch" state={stage === "geometry" ? "active" : (readyForTrace ? "done" : "pending")} />
          </div>

          {current.warning && (
            <div className="rounded-md border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 text-[11px] text-[#FF3B30]">
              {current.warning}
            </div>
          )}

          {/* --------------- Step 1/2 · Circle scans --------------- */}
          <div className="grid gap-3 lg:grid-cols-2">
            {[
              { which: "a", shots: current.circle_a_shots, fit: current.circle_a_fit, title: "Circle A" },
              { which: "b", shots: current.circle_b_shots, fit: current.circle_b_fit, title: "Circle B" },
            ].map(({ which, shots, fit, title }) => {
              const rmse = fit?.rmse_mm ?? null;
              const bad = rmse != null && rmse > RMSE_WARN_MM;
              const caution = rmse != null && rmse > RMSE_CAUTION_MM && !bad;
              const panelCls = bad
                ? "border-[#FF3B30]/60 bg-[#FF3B30]/5"
                : caution
                  ? "border-[#F5A623]/40 bg-[#F5A623]/5"
                  : "border-[#2A2E39] bg-[#14161A]";
              const rmseCls = bad
                ? "text-[#FF3B30]"
                : caution
                  ? "text-[#F5A623]"
                  : "text-[#8DC63F]";
              // Worst residual — used in the warning banner so the
              // surveyor knows what number to beat by re-shooting.
              const worst = fit
                ? shots.reduce((max, sh) => {
                    const r = shotResidualMm(sh, fit) ?? 0;
                    return r > max ? r : max;
                  }, 0)
                : 0;
              return (
                <div key={which} className={`rounded-md border p-3 ${panelCls}`} data-testid={`template-panel-${which}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-[#8B939A]">
                      {title} — {shots.length} shot{shots.length !== 1 ? "s" : ""}
                    </div>
                    {fit && (
                      <div className={`text-[10px] font-bold ${rmseCls}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        Ø {(fit.radius_m * 2000).toFixed(1)} mm · RMSE {fit.rmse_mm.toFixed(2)} mm
                      </div>
                    )}
                  </div>
                  {bad && (
                    <div
                      data-testid={`template-rmse-warn-${which}`}
                      className="mb-2 rounded-sm border border-[#FF3B30]/60 bg-[#FF3B30]/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#FF3B30]"
                      style={{ fontFamily: "Chakra Petch, sans-serif" }}
                    >
                      ⚠ Circle fit off by {rmse.toFixed(1)} mm · worst shot {worst.toFixed(1)} mm · re-shoot the reddest row
                    </div>
                  )}
                  {caution && (
                    <div
                      data-testid={`template-rmse-caution-${which}`}
                      className="mb-2 rounded-sm border border-[#F5A623]/40 bg-[#F5A623]/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#F5A623]"
                      style={{ fontFamily: "Chakra Petch, sans-serif" }}
                    >
                      RMSE {rmse.toFixed(1)} mm — acceptable but tight, consider re-shooting the amber row
                    </div>
                  )}
                  <div className="space-y-1">
                    {shots.length === 0 ? (
                      <div className="rounded-sm border border-dashed border-[#2A2E39] bg-[#0A0B0E] p-2 text-center text-[10px] text-[#5A6065]">
                        No shots yet — take ≥3 points around the pipe on the {title === "Circle A" ? "left" : "right"} side of the hole.
                      </div>
                    ) : (
                      shots.map((sh) => (
                        <ShotRow key={sh.id} shot={sh}
                          residualMm={shotResidualMm(sh, fit)}
                          onDelete={() => deleteCircleShot(which, sh.id)}
                          testid={`template-del-${which}-${sh.id}`} />
                      ))
                    )}
                  </div>
                  {!fit && shots.length > 0 && shots.length < 3 && (
                    <div className="mt-2 text-[10px] text-[#F5A623]">Need {3 - shots.length} more shot(s)</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* --------------- Step 3 · Host summary --------------- */}
          {hostOk && (
            <div className="rounded-md border border-[#8DC63F]/40 bg-[#8DC63F]/5 p-3">
              <div className="mb-2 text-[10px] uppercase tracking-widest text-[#8DC63F]">
                Host derived · {current.host_orientation.toUpperCase()}
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">OD</div>
                  <div className="mt-0.5 text-white">{current.resolved_host_od_mm?.toFixed(1)} mm</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">A ↔ B</div>
                  <div className="mt-0.5 text-white">{(current.host_axis_length_m * 1000).toFixed(0)} mm</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">Bearing</div>
                  <div className="mt-0.5 text-white">{current.host_axis_bearing_deg?.toFixed(1)}°</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">Elevation</div>
                  <div className="mt-0.5 text-white">{current.host_axis_elevation_deg?.toFixed(1)}°</div>
                </div>
              </div>
            </div>
          )}

          {/* --------------- Step 4 · Intersection --------------- */}
          {hostOk && (
            <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-[#8B939A]">
                  Intersection centreline
                </div>
                {intersectOk && (
                  <button
                    data-testid="template-intersect-clear" onClick={clearIntersection}
                    className="rounded-sm border border-[#FF3B30]/40 px-2 py-0.5 text-[10px] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                  >
                    <RotateCcw size={10} className="inline" /> Re-do
                  </button>
                )}
              </div>
              {intersectOk ? (
                <div className="text-[11px] text-white" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  raw: {current.intersection_point.x.toFixed(3)} / {current.intersection_point.y.toFixed(3)} / {current.intersection_point.z.toFixed(3)}
                  <br />
                  snapped to host CL: {current.resolved_origin.x.toFixed(3)} / {current.resolved_origin.y.toFixed(3)} / {current.resolved_origin.z.toFixed(3)}
                </div>
              ) : (
                <div className="text-[11px] text-[#8B939A]">
                  Take one shot at the CL of where the branch pipe crosses the host. It will be projected onto the host axis.
                </div>
              )}
            </div>
          )}

          {/* --------------- Step 5 · Branch geometry --------------- */}
          {intersectOk && (
            <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3">
              <div className="mb-2 text-[10px] uppercase tracking-widest text-[#8B939A]">
                Branch geometry
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <label className="flex flex-col text-[10px] uppercase tracking-widest text-[#5A6065]">
                  Cut OD (mm)
                  <input type="number" step="1" value={current.cut_tube_od_mm}
                    data-testid="template-cut-od"
                    onChange={(e) => updateGeom({ cut_tube_od_mm: parseFloat(e.target.value) || 100 })}
                    className="mt-1 rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-1 font-mono text-sm text-white" />
                </label>
                <label className="flex flex-col text-[10px] uppercase tracking-widest text-[#5A6065]">
                  Angle (° from host)
                  <input type="number" step="0.5" value={current.intersect_angle_deg}
                    data-testid="template-angle"
                    onChange={(e) => updateGeom({ intersect_angle_deg: parseFloat(e.target.value) || 90 })}
                    className="mt-1 rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-1 font-mono text-sm text-white" />
                </label>
                <div className="flex flex-col text-[10px] uppercase tracking-widest text-[#5A6065]">
                  Direction
                  <div className="mt-1 grid grid-cols-2 gap-1">
                    {["toward_a", "toward_b"].map((d) => (
                      <button key={d}
                        data-testid={`template-dir-${d}`}
                        onClick={() => updateGeom({ branch_direction: d })}
                        className={`rounded-sm border py-1 text-[10px] font-bold uppercase tracking-widest ${
                          current.branch_direction === d
                            ? "border-[#00E1C7] bg-[#00E1C7]/10 text-[#00E1C7]"
                            : "border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A] hover:border-[#00E1C7]/60"
                        }`}>
                        {d === "toward_a" ? "◀ A" : "B ▶"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <p className="mt-2 text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                Angle 90° = perpendicular (branch pokes straight out of the host). If you type e.g. 60°, pick which end of the host the tip leans towards.
              </p>
            </div>
          )}

          {/* --------------- 3-D preview --------------- */}
          {hostOk && (
            <div>
              <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-widest text-[#8B939A]">
                <span>3D preview</span>
                <span className="text-[#5A6065]">drag = rotate · pinch = zoom</span>
              </div>
              <div className="h-72 w-full overflow-hidden rounded-md border border-[#2A2E39] bg-[#0A0B0E]">
                <Scene3D session={current} />
              </div>
            </div>
          )}

          {/* --------------- Unfolded template + stats --------------- */}
          {readyForTrace && (
            <>
              <TemplatePreview
                points={current.template_2d}
                hostOD={current.host_tube_od_mm}
                cutOD={current.cut_tube_od_mm}
              />
              <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-3 text-[11px]">
                <div className="mb-2 text-[10px] uppercase tracking-widest text-[#8B939A]">Cut curve stats</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">Peak length</div>
                    <div className="mt-0.5 font-mono text-white">
                      {current.template_2d.length ? Math.max(...current.template_2d.map((p) => p[1])).toFixed(1) : "—"} mm
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">Notch depth</div>
                    <div className="mt-0.5 font-mono text-white">
                      {current.template_2d.length
                        ? (Math.max(...current.template_2d.map((p) => p[1])) - Math.min(...current.template_2d.map((p) => p[1]))).toFixed(1)
                        : "—"} mm
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">Points</div>
                    <div className="mt-0.5 font-mono text-white">{current.points_world.length}</div>
                  </div>
                </div>
              </div>
            </>
          )}
          </PrintReportShell>
        </div>
      }
      right={
        <>
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrent(null)} data-testid="template-back-session"
              className="flex-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E]">
              ← Sessions
            </button>
          </div>

          {/* Circle A / B shot buttons.
              Stages: "a" → "b" → "intersection" → "geometry".
              User advances via the "Done" button on each stage — no
              auto-advance so they control exactly how many points to
              take on each ring. */}
          {stage === "a" && (
            <>
              <button
                data-testid="template-take-a"
                disabled={busy}
                onClick={() => takeCircleShot("a")}
                className="min-h-[64px] w-full rounded-md border border-[#009E8E] bg-gradient-to-br from-[#009E8E]/20 to-[#005a52]/20 py-3 text-sm font-black uppercase tracking-widest text-[#00E1C7] disabled:opacity-40"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                <div className="flex items-center justify-center gap-2">
                  <Crosshair size={18} strokeWidth={2.5} />
                  Take Circle A shot ({nA})
                </div>
              </button>
              {nA >= 3 && (
                <button
                  data-testid="template-done-a"
                  onClick={() => advanceStage("b")}
                  className="min-h-[52px] w-full rounded-md border border-[#8DC63F] bg-gradient-to-br from-[#8DC63F]/20 to-[#3d5c1a]/20 py-3 text-sm font-black uppercase tracking-widest text-[#8DC63F]"
                  style={{ fontFamily: "Chakra Petch, sans-serif" }}
                >
                  ✓ Done Circle A · Move to B →
                </button>
              )}
              {nA >= 3 && !aFitOk && (
                <div className="rounded-sm border border-[#F5A623]/40 bg-[#F5A623]/10 p-2 text-[10px] text-[#F5A623]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  Fit hasn&apos;t converged yet — {current.warning || "check the residuals on the left"}. You can still move on if you&apos;re happy.
                </div>
              )}
            </>
          )}

          {stage === "b" && (
            <>
              <button
                data-testid="template-take-b"
                disabled={busy}
                onClick={() => takeCircleShot("b")}
                className="min-h-[64px] w-full rounded-md border border-[#009E8E] bg-gradient-to-br from-[#009E8E]/20 to-[#005a52]/20 py-3 text-sm font-black uppercase tracking-widest text-[#00E1C7] disabled:opacity-40"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                <div className="flex items-center justify-center gap-2">
                  <Crosshair size={18} strokeWidth={2.5} />
                  Take Circle B shot ({nB})
                </div>
              </button>
              {nB >= 3 && (
                <button
                  data-testid="template-done-b"
                  onClick={() => advanceStage("intersection")}
                  className="min-h-[52px] w-full rounded-md border border-[#8DC63F] bg-gradient-to-br from-[#8DC63F]/20 to-[#3d5c1a]/20 py-3 text-sm font-black uppercase tracking-widest text-[#8DC63F]"
                  style={{ fontFamily: "Chakra Petch, sans-serif" }}
                >
                  ✓ Done Circle B · Measure Intersection →
                </button>
              )}
              <button
                data-testid="template-back-to-a"
                onClick={() => advanceStage("a")}
                className="w-full rounded-sm border border-[#2A2E39] px-2 py-1 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#00E1C7]/60"
              >
                ← Back to Circle A
              </button>
            </>
          )}

          {stage === "intersection" && !intersectOk && (
            <>
              <button
                data-testid="template-take-intersect"
                disabled={busy}
                onClick={async () => {
                  await takeIntersection();
                  advanceStage("geometry");
                }}
                className="min-h-[64px] w-full rounded-md border border-[#F5A623] bg-gradient-to-br from-[#F5A623]/20 to-[#8B5A00]/20 py-3 text-sm font-black uppercase tracking-widest text-[#F5A623] disabled:opacity-40"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                <div className="flex items-center justify-center gap-2">
                  <Crosshair size={18} strokeWidth={2.5} />
                  Measure Intersection CL
                </div>
              </button>
              <button
                data-testid="template-back-to-b"
                onClick={() => advanceStage("b")}
                className="w-full rounded-sm border border-[#2A2E39] px-2 py-1 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#00E1C7]/60"
              >
                ← Back to Circle B
              </button>
            </>
          )}

          {(stage === "geometry" || (stage === "intersection" && intersectOk)) && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                data-testid="template-back-to-intersect"
                onClick={() => { clearIntersection(); advanceStage("intersection"); }}
                className="rounded-sm border border-[#2A2E39] px-2 py-1 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#00E1C7]/60"
              >
                ← Re-do intersection
              </button>
              <button
                data-testid="template-restart-scan"
                onClick={() => advanceStage("a")}
                className="rounded-sm border border-[#2A2E39] px-2 py-1 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#00E1C7]/60"
              >
                ← Back to scans
              </button>
            </div>
          )}

          {/* Trace */}
          <button
            data-testid="template-run-trace"
            disabled={tracing || !readyForTrace}
            onClick={runTrace}
            className="min-h-[80px] w-full rounded-md border border-[#00E1C7] bg-gradient-to-br from-[#00E1C7]/20 to-[#005a52]/20 py-4 text-lg font-black uppercase tracking-widest text-[#00E1C7] disabled:opacity-40"
            style={{ fontFamily: "Chakra Petch, sans-serif" }}
          >
            <div className="flex items-center justify-center gap-2">
              <Aperture size={22} strokeWidth={2.5} />
              {tracing
                ? `Tracing… ${tracingIdx + 1}/${current.points_world.length}`
                : "Laser Trace Cut Line"}
            </div>
          </button>
          <AimPad tool="template" sessionId={current.id} />
        </>
      }
    />
  );
}
